/**
 * Anchor scrolling with the site's own easing.
 *
 * CSS `scroll-behavior: smooth` uses the browser's curve and a duration that
 * grows with distance — a hero-to-contact jump took nearly three seconds to
 * settle, which reads as sluggish rather than deliberate. This clamps the
 * duration and applies the same cubic-bezier(0.22, 1, 0.36, 1) used by the
 * reveal animations and the link underlines, so a short jump and a long one
 * feel like the same gesture.
 */

const HEADER_OFFSET = 100; // matches scroll-padding-top in index.css
const MIN_DURATION = 480;
const MAX_DURATION = 820;

/** cubic-bezier(0.22, 1, 0.36, 1) — "ease-out-quint"-ish, calm and decisive. */
function easeOutQuint(t: number): number {
  return 1 - Math.pow(1 - t, 5);
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function scrollToElement(target: HTMLElement | null): void {
  if (!target) return;

  const start = window.scrollY;
  const end = Math.max(0, start + target.getBoundingClientRect().top - HEADER_OFFSET);
  const distance = end - start;

  if (prefersReducedMotion() || Math.abs(distance) < 2) {
    window.scrollTo({ top: end, behavior: 'instant' as ScrollBehavior });
    return;
  }

  // Duration grows with distance but is clamped, so the far jumps stop
  // dragging and the near ones still feel like movement rather than a cut.
  const duration = Math.min(MAX_DURATION, Math.max(MIN_DURATION, Math.abs(distance) * 0.35));
  const startTime = performance.now();

  const step = (now: number) => {
    const elapsed = now - startTime;
    const t = Math.min(1, elapsed / duration);
    // 'instant' per frame: 'auto' resolves to the CSS scroll-behavior, which is
    // smooth here — each frame would start its own animation and fight ours.
    window.scrollTo({ top: start + distance * easeOutQuint(t), behavior: 'instant' as ScrollBehavior });
    if (t < 1) requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
}

export function scrollToId(id: string): void {
  scrollToElement(document.getElementById(id));
}
